import { Workbook } from './engine';
import { adjustFormula } from './parser';
import { Cell, cellKey, cellName, colName, isErr } from './types';

export interface GridSelection {
  sr: number; sc: number; er: number; ec: number;
  ar: number; ac: number;
}

export interface GridTheme {
  bg: string; gridLine: string; text: string; headerBg: string; headerText: string;
  headerBorder: string; selBg: string; selBorder: string; activeBorder: string;
  errText: string; boolText: string; headerActiveBg: string;
}

export const DARK_THEME: GridTheme = {
  bg: '#0d1117', gridLine: '#21262d', text: '#c9d1d9', headerBg: '#161b22',
  headerText: '#8b949e', headerBorder: '#30363d', selBg: 'rgba(56,139,253,0.15)',
  selBorder: '#388bfd', activeBorder: '#58a6ff', errText: '#f85149', boolText: '#d2a8ff',
  headerActiveBg: '#1f6feb33',
};

interface UndoEntry {
  si: number;
  changes: Array<{ r: number; c: number; before: Cell | undefined; after: Cell | undefined }>;
}

const DEF_COL_W = 100;
const DEF_ROW_H = 26;
const HEADER_W = 48;
const HEADER_H = 26;
const MIN_COL_W = 32;
const MIN_ROW_H = 18;

export interface GridOptions {
  theme?: Partial<GridTheme>;
  onSelectionChange?: (sel: GridSelection) => void;
  onEditStateChange?: (editing: boolean, text: string) => void;
  onModelChange?: () => void;
}

export class SheetGrid {
  wb: Workbook;
  si = 0;
  sel: GridSelection = { sr: 0, sc: 0, er: 0, ec: 0, ar: 0, ac: 0 };
  private container: HTMLElement;
  private scroller: HTMLDivElement;
  private spacer: HTMLDivElement;
  private canvas: HTMLCanvasElement;
  private input: HTMLTextAreaElement;
  private clip: HTMLTextAreaElement;
  private theme: GridTheme;
  private opts: GridOptions;
  private rowCum: Float64Array = new Float64Array(0);
  private colCum: Float64Array = new Float64Array(0);
  private nRows = 0;
  private nCols = 0;
  private dirty = false;
  private raf = 0;
  private editing = false;
  private editR = 0;
  private editC = 0;
  private dragMode: 'none' | 'select' | 'colresize' | 'rowresize' | 'fill' | 'colsel' | 'rowsel' = 'none';
  private resizeIdx = -1;
  private resizeStart = 0;
  private resizeOrig = 0;
  private fillTarget: { sr: number; sc: number; er: number; ec: number } | null = null;
  private undoStack: UndoEntry[] = [];
  private redoStack: UndoEntry[] = [];
  private internalClip: { text: string; cells: Array<Array<{ f?: string; v?: Cell['v']; s?: Cell['s'] }>>; origin: { si: number; sr: number; sc: number }; cut: boolean } | null = null;
  private ro: ResizeObserver;
  private destroyed = false;
  private unsub: () => void;

  constructor(container: HTMLElement, wb: Workbook, opts: GridOptions = {}) {
    this.container = container;
    this.wb = wb;
    this.opts = opts;
    this.theme = { ...DARK_THEME, ...(opts.theme || {}) };

    container.style.position = 'relative';
    container.style.overflow = 'hidden';

    this.scroller = document.createElement('div');
    Object.assign(this.scroller.style, {
      position: 'absolute', inset: '0', overflow: 'auto', outline: 'none',
    } as CSSStyleDeclaration);
    this.scroller.tabIndex = 0;
    this.spacer = document.createElement('div');
    this.spacer.style.pointerEvents = 'none';
    this.scroller.appendChild(this.spacer);

    this.canvas = document.createElement('canvas');
    Object.assign(this.canvas.style, {
      position: 'absolute', top: '0', left: '0', pointerEvents: 'none',
    } as CSSStyleDeclaration);

    this.input = document.createElement('textarea');
    Object.assign(this.input.style, {
      position: 'absolute', display: 'none', resize: 'none', border: `2px solid ${this.theme.activeBorder}`,
      background: this.theme.bg, color: this.theme.text, font: '13px ui-sans-serif, system-ui, sans-serif',
      padding: '3px 4px', margin: '0', outline: 'none', zIndex: '5', overflow: 'hidden', whiteSpace: 'pre',
      boxSizing: 'border-box', lineHeight: '18px',
    } as CSSStyleDeclaration);

    this.clip = document.createElement('textarea');
    Object.assign(this.clip.style, {
      position: 'absolute', left: '-9999px', top: '0', width: '1px', height: '1px', opacity: '0',
    } as CSSStyleDeclaration);

    container.appendChild(this.scroller);
    container.appendChild(this.canvas);
    container.appendChild(this.input);
    container.appendChild(this.clip);

    this.rebuildMetrics();

    this.scroller.addEventListener('scroll', this.requestRender);
    this.scroller.addEventListener('mousedown', this.onMouseDown);
    this.scroller.addEventListener('mousemove', this.onHoverMove);
    this.scroller.addEventListener('dblclick', this.onDblClick);
    this.scroller.addEventListener('keydown', this.onKeyDown);
    // Copy/cut events never fire on a non-editable div, so clipboard events are
    // handled at document level, guarded on grid focus.
    document.addEventListener('copy', this.onCopy);
    document.addEventListener('cut', this.onCut);
    document.addEventListener('paste', this.onPaste);
    window.addEventListener('mousemove', this.onWindowMove);
    window.addEventListener('mouseup', this.onWindowUp);
    this.input.addEventListener('keydown', this.onEditorKey);
    this.input.addEventListener('input', () => {
      this.autosizeEditor();
      this.opts.onEditStateChange?.(true, this.input.value);
    });

    const listener = () => {
      this.rebuildMetrics();
      this.requestRender();
      this.opts.onModelChange?.();
    };
    wb.listeners.push(listener);
    this.unsub = () => {
      const i = wb.listeners.indexOf(listener);
      if (i >= 0) wb.listeners.splice(i, 1);
    };

    this.ro = new ResizeObserver(() => {
      this.sizeCanvas();
      this.requestRender();
    });
    this.ro.observe(container);
    this.sizeCanvas();
    this.requestRender();
  }

  destroy() {
    this.destroyed = true;
    this.unsub();
    this.ro.disconnect();
    document.removeEventListener('copy', this.onCopy);
    document.removeEventListener('cut', this.onCut);
    document.removeEventListener('paste', this.onPaste);
    window.removeEventListener('mousemove', this.onWindowMove);
    window.removeEventListener('mouseup', this.onWindowUp);
    if (this.raf) cancelAnimationFrame(this.raf);
    this.container.replaceChildren();
  }

  focus() {
    this.scroller.focus({ preventScroll: true });
  }

  setSheet(si: number, force = false) {
    if (si === this.si && !force) return;
    this.cancelEdit();
    this.si = si;
    this.sel = { sr: 0, sc: 0, er: 0, ec: 0, ar: 0, ac: 0 };
    this.scroller.scrollTop = 0;
    this.scroller.scrollLeft = 0;
    this.rebuildMetrics();
    this.requestRender();
    this.emitSelection();
  }

  private sheet() {
    return this.wb.sheet(this.si);
  }

  private rebuildMetrics() {
    const sheet = this.sheet();
    const used = this.wb.usedRange(this.si);
    this.nRows = Math.max(sheet.rows || 0, used.rows + 100, 200);
    this.nCols = Math.max(sheet.cols || 0, used.cols + 10, 26);
    this.nRows = Math.min(this.nRows, 100000);
    this.nCols = Math.min(this.nCols, 1000);
    this.rowCum = new Float64Array(this.nRows + 1);
    const rowH = sheet.rowH || {};
    let acc = 0;
    for (let r = 0; r < this.nRows; r++) {
      this.rowCum[r] = acc;
      acc += rowH[r] || DEF_ROW_H;
    }
    this.rowCum[this.nRows] = acc;
    this.colCum = new Float64Array(this.nCols + 1);
    const colW = sheet.colW || {};
    let accC = 0;
    for (let c = 0; c < this.nCols; c++) {
      this.colCum[c] = accC;
      accC += colW[c] || DEF_COL_W;
    }
    this.colCum[this.nCols] = accC;
    this.spacer.style.width = HEADER_W + accC + 'px';
    this.spacer.style.height = HEADER_H + acc + 'px';
  }

  private rowH(r: number) {
    return this.rowCum[r + 1] - this.rowCum[r];
  }

  private colW(c: number) {
    return this.colCum[c + 1] - this.colCum[c];
  }

  private findIndex(cum: Float64Array, pos: number): number {
    let lo = 0;
    let hi = cum.length - 2;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (cum[mid] <= pos) lo = mid;
      else hi = mid - 1;
    }
    return lo;
  }

  private sizeCanvas() {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = Math.max(1, Math.floor(w * dpr));
    this.canvas.height = Math.max(1, Math.floor(h * dpr));
    this.canvas.style.width = w + 'px';
    this.canvas.style.height = h + 'px';
  }

  requestRender = () => {
    if (this.dirty || this.destroyed) return;
    this.dirty = true;
    this.raf = requestAnimationFrame(() => {
      this.dirty = false;
      this.render();
      if (this.editing) this.positionEditor();
    });
  };

  private render() {
    if (this.destroyed) return;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const W = this.container.clientWidth;
    const H = this.container.clientHeight;
    const t = this.theme;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = t.bg;
    ctx.fillRect(0, 0, W, H);

    const sx = this.scroller.scrollLeft;
    const sy = this.scroller.scrollTop;
    const bodyW = W - HEADER_W;
    const bodyH = H - HEADER_H;
    const r0 = this.findIndex(this.rowCum, sy);
    const r1 = Math.min(this.nRows - 1, this.findIndex(this.rowCum, sy + bodyH));
    const c0 = this.findIndex(this.colCum, sx);
    const c1 = Math.min(this.nCols - 1, this.findIndex(this.colCum, sx + bodyW));
    const sheet = this.sheet();
    const cells = sheet.cells;

    const xOf = (c: number) => HEADER_W + this.colCum[c] - sx;
    const yOf = (r: number) => HEADER_H + this.rowCum[r] - sy;

    const sel = this.sel;
    const selX = xOf(sel.sc);
    const selY = yOf(sel.sr);
    const selW = this.colCum[sel.ec + 1] - this.colCum[sel.sc];
    const selH = this.rowCum[sel.er + 1] - this.rowCum[sel.sr];

    ctx.save();
    ctx.beginPath();
    ctx.rect(HEADER_W, HEADER_H, bodyW, bodyH);
    ctx.clip();

    if (!(sel.sr === sel.er && sel.sc === sel.ec)) {
      ctx.fillStyle = t.selBg;
      ctx.fillRect(selX, selY, selW, selH);
    }

    ctx.strokeStyle = t.gridLine;
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let r = r0; r <= r1 + 1 && r <= this.nRows; r++) {
      const y = Math.round(yOf(r)) + 0.5;
      ctx.moveTo(HEADER_W, y);
      ctx.lineTo(W, y);
    }
    for (let c = c0; c <= c1 + 1 && c <= this.nCols; c++) {
      const x = Math.round(xOf(c)) + 0.5;
      ctx.moveTo(x, HEADER_H);
      ctx.lineTo(x, H);
    }
    ctx.stroke();

    const baseFont = '13px ui-sans-serif, system-ui, sans-serif';
    ctx.textBaseline = 'middle';
    for (let r = r0; r <= r1; r++) {
      const y = yOf(r);
      const h = this.rowH(r);
      for (let c = c0; c <= c1; c++) {
        const cell = cells[cellKey(r, c)];
        if (!cell) continue;
        const x = xOf(c);
        const w = this.colW(c);
        const s = cell.s;
        if (s?.bg) {
          ctx.fillStyle = s.bg;
          ctx.fillRect(x + 1, y + 1, w - 1, h - 1);
        }
        if (cell.v === undefined && cell.f === undefined) continue;
        const val = this.wb.getValue(this.si, r, c);
        if (val == null || val === '') continue;
        const text = this.wb.getDisplay(this.si, r, c);
        if (!text) continue;
        const err = isErr(val);
        const num = typeof val === 'number';
        const bool = typeof val === 'boolean';
        ctx.font = `${s?.italic ? 'italic ' : ''}${s?.bold ? '600 ' : ''}${baseFont}`;
        ctx.fillStyle = err ? t.errText : bool ? t.boolText : s?.color || t.text;
        const align = s?.align || (num || err ? 'right' : bool ? 'center' : 'left');
        ctx.save();
        ctx.beginPath();
        ctx.rect(x + 1, y + 1, Math.max(0, w - 2), Math.max(0, h - 2));
        ctx.clip();
        const ty = y + h / 2 + 0.5;
        if (align === 'right') {
          ctx.textAlign = 'right';
          ctx.fillText(text, x + w - 5, ty);
        } else if (align === 'center') {
          ctx.textAlign = 'center';
          ctx.fillText(text, x + w / 2, ty);
        } else {
          ctx.textAlign = 'left';
          ctx.fillText(text, x + 5, ty);
        }
        ctx.restore();
      }
    }

    ctx.strokeStyle = t.selBorder;
    ctx.lineWidth = 1.5;
    ctx.strokeRect(selX + 0.5, selY + 0.5, selW - 1, selH - 1);
    const aX = xOf(sel.ac);
    const aY = yOf(sel.ar);
    ctx.strokeStyle = t.activeBorder;
    ctx.lineWidth = 2;
    ctx.strokeRect(aX + 1, aY + 1, this.colW(sel.ac) - 2, this.rowH(sel.ar) - 2);

    ctx.fillStyle = t.activeBorder;
    ctx.fillRect(selX + selW - 4, selY + selH - 4, 6, 6);

    if (this.fillTarget) {
      const ft = this.fillTarget;
      ctx.setLineDash([4, 3]);
      ctx.strokeStyle = t.activeBorder;
      ctx.lineWidth = 1.5;
      ctx.strokeRect(
        xOf(ft.sc) + 0.5, yOf(ft.sr) + 0.5,
        this.colCum[ft.ec + 1] - this.colCum[ft.sc] - 1,
        this.rowCum[ft.er + 1] - this.rowCum[ft.sr] - 1,
      );
      ctx.setLineDash([]);
    }
    ctx.restore();

    ctx.fillStyle = t.headerBg;
    ctx.fillRect(0, 0, W, HEADER_H);
    ctx.fillRect(0, 0, HEADER_W, H);
    ctx.strokeStyle = t.headerBorder;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, HEADER_H - 0.5);
    ctx.lineTo(W, HEADER_H - 0.5);
    ctx.moveTo(HEADER_W - 0.5, 0);
    ctx.lineTo(HEADER_W - 0.5, H);
    ctx.stroke();

    ctx.font = '11px ui-sans-serif, system-ui, sans-serif';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.save();
    ctx.beginPath();
    ctx.rect(HEADER_W, 0, bodyW, HEADER_H);
    ctx.clip();
    for (let c = c0; c <= c1; c++) {
      const x = xOf(c);
      const w = this.colW(c);
      if (c >= sel.sc && c <= sel.ec) {
        ctx.fillStyle = t.headerActiveBg;
        ctx.fillRect(x, 0, w, HEADER_H);
      }
      ctx.fillStyle = t.headerText;
      ctx.fillText(colName(c), x + w / 2, HEADER_H / 2 + 0.5);
      ctx.strokeStyle = t.headerBorder;
      ctx.beginPath();
      ctx.moveTo(Math.round(x + w) + 0.5, 0);
      ctx.lineTo(Math.round(x + w) + 0.5, HEADER_H);
      ctx.stroke();
    }
    ctx.restore();
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, HEADER_H, HEADER_W, bodyH);
    ctx.clip();
    for (let r = r0; r <= r1; r++) {
      const y = yOf(r);
      const h = this.rowH(r);
      if (r >= sel.sr && r <= sel.er) {
        ctx.fillStyle = t.headerActiveBg;
        ctx.fillRect(0, y, HEADER_W, h);
      }
      ctx.fillStyle = t.headerText;
      ctx.fillText(String(r + 1), HEADER_W / 2, y + h / 2 + 0.5);
      ctx.strokeStyle = t.headerBorder;
      ctx.beginPath();
      ctx.moveTo(0, Math.round(y + h) + 0.5);
      ctx.lineTo(HEADER_W, Math.round(y + h) + 0.5);
      ctx.stroke();
    }
    ctx.restore();
    ctx.fillStyle = t.headerBg;
    ctx.fillRect(0, 0, HEADER_W, HEADER_H);
  }

  private hitTest(e: MouseEvent): { zone: 'corner' | 'colhead' | 'rowhead' | 'body'; r: number; c: number; x: number; y: number } {
    const rect = this.container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const cx = x - HEADER_W + this.scroller.scrollLeft;
    const cy = y - HEADER_H + this.scroller.scrollTop;
    const c = Math.max(0, Math.min(this.nCols - 1, this.findIndex(this.colCum, Math.max(0, cx))));
    const r = Math.max(0, Math.min(this.nRows - 1, this.findIndex(this.rowCum, Math.max(0, cy))));
    if (x < HEADER_W && y < HEADER_H) return { zone: 'corner', r, c, x, y };
    if (y < HEADER_H) return { zone: 'colhead', r, c, x, y };
    if (x < HEADER_W) return { zone: 'rowhead', r, c, x, y };
    return { zone: 'body', r, c, x, y };
  }

  private colEdgeAt(x: number): number {
    const cx = x - HEADER_W + this.scroller.scrollLeft;
    const c = this.findIndex(this.colCum, Math.max(0, cx));
    if (Math.abs(cx - this.colCum[c + 1]) < 4) return c;
    if (c > 0 && Math.abs(cx - this.colCum[c]) < 4) return c - 1;
    return -1;
  }

  private rowEdgeAt(y: number): number {
    const cy = y - HEADER_H + this.scroller.scrollTop;
    const r = this.findIndex(this.rowCum, Math.max(0, cy));
    if (Math.abs(cy - this.rowCum[r + 1]) < 4) return r;
    if (r > 0 && Math.abs(cy - this.rowCum[r]) < 4) return r - 1;
    return -1;
  }

  private onHoverMove = (e: MouseEvent) => {
    if (this.dragMode !== 'none') return;
    const hit = this.hitTest(e);
    let cursor = 'cell';
    if (hit.zone === 'colhead' && this.colEdgeAt(hit.x) >= 0) cursor = 'col-resize';
    else if (hit.zone === 'rowhead' && this.rowEdgeAt(hit.y) >= 0) cursor = 'row-resize';
    else if (hit.zone === 'colhead' || hit.zone === 'rowhead') cursor = 'pointer';
    else if (hit.zone === 'body') {
      const rect = this.container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const hx = HEADER_W + this.colCum[this.sel.ec + 1] - this.scroller.scrollLeft;
      const hy = HEADER_H + this.rowCum[this.sel.er + 1] - this.scroller.scrollTop;
      if (Math.abs(x - hx) < 6 && Math.abs(y - hy) < 6) cursor = 'crosshair';
    }
    this.scroller.style.cursor = cursor;
  };

  private onMouseDown = (e: MouseEvent) => {
    if (e.button !== 0) return;
    const hit = this.hitTest(e);
    this.commitEdit();
    this.focus();
    e.preventDefault();

    if (hit.zone === 'corner') {
      this.setSelection(0, 0, this.nRows - 1, this.nCols - 1, 0, 0);
      return;
    }
    if (hit.zone === 'colhead') {
      const edge = this.colEdgeAt(hit.x);
      if (edge >= 0) {
        this.dragMode = 'colresize';
        this.resizeIdx = edge;
        this.resizeStart = e.clientX;
        this.resizeOrig = this.colW(edge);
        return;
      }
      this.dragMode = 'colsel';
      this.setSelection(0, hit.c, this.nRows - 1, hit.c, 0, hit.c);
      return;
    }
    if (hit.zone === 'rowhead') {
      const edge = this.rowEdgeAt(hit.y);
      if (edge >= 0) {
        this.dragMode = 'rowresize';
        this.resizeIdx = edge;
        this.resizeStart = e.clientY;
        this.resizeOrig = this.rowH(edge);
        return;
      }
      this.dragMode = 'rowsel';
      this.setSelection(hit.r, 0, hit.r, this.nCols - 1, hit.r, 0);
      return;
    }

    const rect = this.container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const hx = HEADER_W + this.colCum[this.sel.ec + 1] - this.scroller.scrollLeft;
    const hy = HEADER_H + this.rowCum[this.sel.er + 1] - this.scroller.scrollTop;
    if (Math.abs(x - hx) < 6 && Math.abs(y - hy) < 6) {
      this.dragMode = 'fill';
      this.fillTarget = { sr: this.sel.sr, sc: this.sel.sc, er: this.sel.er, ec: this.sel.ec };
      return;
    }

    if (e.shiftKey) {
      this.extendTo(hit.r, hit.c);
    } else {
      this.setSelection(hit.r, hit.c, hit.r, hit.c, hit.r, hit.c);
    }
    this.dragMode = 'select';
  };

  private onWindowMove = (e: MouseEvent) => {
    if (this.dragMode === 'none') return;
    if (this.dragMode === 'colresize') {
      const w = Math.max(MIN_COL_W, this.resizeOrig + e.clientX - this.resizeStart);
      const sheet = this.sheet();
      if (!sheet.colW) sheet.colW = {};
      sheet.colW[this.resizeIdx] = Math.round(w);
      this.rebuildMetrics();
      this.requestRender();
      return;
    }
    if (this.dragMode === 'rowresize') {
      const h = Math.max(MIN_ROW_H, this.resizeOrig + e.clientY - this.resizeStart);
      const sheet = this.sheet();
      if (!sheet.rowH) sheet.rowH = {};
      sheet.rowH[this.resizeIdx] = Math.round(h);
      this.rebuildMetrics();
      this.requestRender();
      return;
    }
    const hit = this.hitTest(e);
    if (this.dragMode === 'select') {
      this.extendTo(hit.r, hit.c);
    } else if (this.dragMode === 'colsel') {
      const sc = Math.min(this.sel.ac, hit.c);
      const ec = Math.max(this.sel.ac, hit.c);
      this.setSelection(0, sc, this.nRows - 1, ec, 0, this.sel.ac);
    } else if (this.dragMode === 'rowsel') {
      const sr = Math.min(this.sel.ar, hit.r);
      const er = Math.max(this.sel.ar, hit.r);
      this.setSelection(sr, 0, er, this.nCols - 1, this.sel.ar, 0);
    } else if (this.dragMode === 'fill') {
      const s = this.sel;
      const dR = hit.r < s.sr ? hit.r - s.sr : hit.r > s.er ? hit.r - s.er : 0;
      const dC = hit.c < s.sc ? hit.c - s.sc : hit.c > s.ec ? hit.c - s.ec : 0;
      if (Math.abs(dR) >= Math.abs(dC)) {
        this.fillTarget = {
          sr: Math.min(s.sr, hit.r), sc: s.sc,
          er: Math.max(s.er, hit.r), ec: s.ec,
        };
      } else {
        this.fillTarget = {
          sr: s.sr, sc: Math.min(s.sc, hit.c),
          er: s.er, ec: Math.max(s.ec, hit.c),
        };
      }
      this.requestRender();
    }
  };

  private onWindowUp = () => {
    if (this.dragMode === 'fill' && this.fillTarget) {
      this.applyFill(this.fillTarget);
      this.fillTarget = null;
      this.requestRender();
    }
    if (this.dragMode === 'colresize' || this.dragMode === 'rowresize') {
      this.opts.onModelChange?.();
    }
    this.dragMode = 'none';
  };

  private onDblClick = (e: MouseEvent) => {
    const hit = this.hitTest(e);
    if (hit.zone !== 'body') return;
    this.setSelection(hit.r, hit.c, hit.r, hit.c, hit.r, hit.c);
    this.beginEdit();
  };

  private setSelection(sr: number, sc: number, er: number, ec: number, ar: number, ac: number) {
    this.sel = { sr, sc, er, ec, ar, ac };
    this.requestRender();
    this.emitSelection();
  }

  private extendTo(r: number, c: number) {
    const { ar, ac } = this.sel;
    this.setSelection(Math.min(ar, r), Math.min(ac, c), Math.max(ar, r), Math.max(ac, c), ar, ac);
  }

  private emitSelection() {
    this.opts.onSelectionChange?.({ ...this.sel });
  }

  private scrollCellIntoView(r: number, c: number) {
    const viewW = this.container.clientWidth - HEADER_W;
    const viewH = this.container.clientHeight - HEADER_H;
    const x0 = this.colCum[c];
    const x1 = this.colCum[c + 1];
    const y0 = this.rowCum[r];
    const y1 = this.rowCum[r + 1];
    if (x0 < this.scroller.scrollLeft) this.scroller.scrollLeft = x0;
    else if (x1 > this.scroller.scrollLeft + viewW) this.scroller.scrollLeft = x1 - viewW;
    if (y0 < this.scroller.scrollTop) this.scroller.scrollTop = y0;
    else if (y1 > this.scroller.scrollTop + viewH) this.scroller.scrollTop = y1 - viewH;
  }

  moveActive(dr: number, dc: number, extend = false) {
    const s = this.sel;
    if (extend) {
      const fr = Math.max(0, Math.min(this.nRows - 1, (s.ar === s.sr ? s.er : s.sr) + dr));
      const fc = Math.max(0, Math.min(this.nCols - 1, (s.ac === s.sc ? s.ec : s.sc) + dc));
      this.extendTo(fr, fc);
      this.scrollCellIntoView(fr, fc);
      return;
    }
    const r = Math.max(0, Math.min(this.nRows - 1, s.ar + dr));
    const c = Math.max(0, Math.min(this.nCols - 1, s.ac + dc));
    this.setSelection(r, c, r, c, r, c);
    this.scrollCellIntoView(r, c);
  }

  private copyToClipboard(cut: boolean) {
    const text = this.captureClip(cut);
    const fallback = () => {
      this.clip.value = text;
      this.clip.focus();
      this.clip.select();
      try { document.execCommand('copy'); } catch { /* clipboard unavailable */ }
      this.focus();
    };
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).catch(fallback);
    else fallback();
    if (cut) this.deleteSelection();
  }

  private onKeyDown = (e: KeyboardEvent) => {
    if (this.editing) return;
    const meta = e.metaKey || e.ctrlKey;
    if (meta && (e.key === 'c' || e.key === 'x')) {
      e.preventDefault();
      this.copyToClipboard(e.key === 'x');
      return;
    }
    if (meta && e.key === 'v') {
      // Let the native paste land in the hidden editable textarea so the
      // document-level paste handler fires in all browsers.
      this.clip.value = '';
      this.clip.focus();
      return;
    }
    if (meta && e.key === 'z') {
      e.preventDefault();
      if (e.shiftKey) this.redo();
      else this.undo();
      return;
    }
    if (meta && e.key === 'y') {
      e.preventDefault();
      this.redo();
      return;
    }
    if (meta && e.key === 'a') {
      e.preventDefault();
      const used = this.wb.usedRange(this.si);
      this.setSelection(0, 0, Math.max(0, used.rows - 1), Math.max(0, used.cols - 1), 0, 0);
      return;
    }
    if (meta && e.key === 'b') {
      e.preventDefault();
      this.toggleStyle('bold');
      return;
    }
    if (meta && e.key === 'i') {
      e.preventDefault();
      this.toggleStyle('italic');
      return;
    }
    switch (e.key) {
      case 'ArrowUp': e.preventDefault(); this.moveActive(meta ? -this.nRows : -1, 0, e.shiftKey); return;
      case 'ArrowDown': e.preventDefault(); this.moveActive(meta ? this.nRows : 1, 0, e.shiftKey); return;
      case 'ArrowLeft': e.preventDefault(); this.moveActive(0, meta ? -this.nCols : -1, e.shiftKey); return;
      case 'ArrowRight': e.preventDefault(); this.moveActive(0, meta ? this.nCols : 1, e.shiftKey); return;
      case 'PageDown': e.preventDefault(); this.moveActive(Math.floor((this.container.clientHeight - HEADER_H) / DEF_ROW_H), 0, e.shiftKey); return;
      case 'PageUp': e.preventDefault(); this.moveActive(-Math.floor((this.container.clientHeight - HEADER_H) / DEF_ROW_H), 0, e.shiftKey); return;
      case 'Home': e.preventDefault(); this.moveActive(0, -this.nCols, e.shiftKey); return;
      case 'Tab': e.preventDefault(); this.moveActive(0, e.shiftKey ? -1 : 1); return;
      case 'Enter': e.preventDefault(); this.beginEdit(); return;
      case 'F2': e.preventDefault(); this.beginEdit(); return;
      case 'Delete':
      case 'Backspace': {
        e.preventDefault();
        this.deleteSelection();
        return;
      }
      case 'Escape': return;
    }
    if (!meta && e.key.length === 1) {
      this.beginEdit(e.key);
      e.preventDefault();
    }
  };

  beginEdit(initial?: string, focusEditor = true) {
    if (this.editing) return;
    this.editing = true;
    this.editR = this.sel.ar;
    this.editC = this.sel.ac;
    this.scrollCellIntoView(this.editR, this.editC);
    this.input.value = initial !== undefined ? initial : this.wb.getInput(this.si, this.editR, this.editC);
    this.input.style.display = 'block';
    this.positionEditor();
    if (focusEditor) {
      this.input.focus();
      this.input.setSelectionRange(this.input.value.length, this.input.value.length);
    }
    this.opts.onEditStateChange?.(true, this.input.value);
  }

  private positionEditor() {
    const x = HEADER_W + this.colCum[this.editC] - this.scroller.scrollLeft;
    const y = HEADER_H + this.rowCum[this.editR] - this.scroller.scrollTop;
    const w = Math.max(this.colW(this.editC), 60);
    const h = this.rowH(this.editR);
    this.input.style.left = x - 1 + 'px';
    this.input.style.top = y - 1 + 'px';
    this.input.style.width = w + 2 + 'px';
    this.input.style.height = h + 2 + 'px';
    this.autosizeEditor();
  }

  private autosizeEditor() {
    const w = Math.max(this.colW(this.editC), 60);
    this.input.style.width = Math.max(w + 2, Math.min(420, this.input.value.length * 7.5 + 20)) + 'px';
  }

  setEditText(text: string) {
    if (!this.editing) {
      this.beginEdit(text);
      return;
    }
    this.input.value = text;
    this.autosizeEditor();
  }

  isEditing() {
    return this.editing;
  }

  private onEditorKey = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.altKey) {
      e.preventDefault();
      this.commitEdit();
      this.moveActive(e.shiftKey ? -1 : 1, 0);
      this.focus();
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      this.commitEdit();
      this.moveActive(0, e.shiftKey ? -1 : 1);
      this.focus();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      this.cancelEdit();
      this.focus();
    }
  };

  commitEdit() {
    if (!this.editing) return;
    const text = this.input.value;
    this.editing = false;
    this.input.style.display = 'none';
    const before = this.snapshotCells(this.si, [[this.editR, this.editC]]);
    this.wb.setCellInput(this.si, this.editR, this.editC, text);
    this.pushUndo(this.si, before);
    this.opts.onEditStateChange?.(false, '');
  }

  cancelEdit() {
    if (!this.editing) return;
    this.editing = false;
    this.input.style.display = 'none';
    this.opts.onEditStateChange?.(false, '');
  }

  private snapshotCells(si: number, coords: Array<[number, number]>): UndoEntry['changes'] {
    const sheet = this.wb.sheet(si);
    return coords.map(([r, c]) => {
      const cell = sheet.cells[cellKey(r, c)];
      return { r, c, before: cell ? JSON.parse(JSON.stringify(cell)) : undefined, after: undefined };
    });
  }

  private pushUndo(si: number, changes: UndoEntry['changes']) {
    const sheet = this.wb.sheet(si);
    for (const ch of changes) {
      const cell = sheet.cells[cellKey(ch.r, ch.c)];
      ch.after = cell ? JSON.parse(JSON.stringify(cell)) : undefined;
    }
    const real = changes.filter((ch) => JSON.stringify(ch.before) !== JSON.stringify(ch.after));
    if (!real.length) return;
    this.undoStack.push({ si, changes: real });
    if (this.undoStack.length > 200) this.undoStack.shift();
    this.redoStack = [];
  }

  undo() {
    const entry = this.undoStack.pop();
    if (!entry) return;
    const sheet = this.wb.sheet(entry.si);
    for (const ch of entry.changes) {
      if (ch.before) sheet.cells[cellKey(ch.r, ch.c)] = JSON.parse(JSON.stringify(ch.before));
      else delete sheet.cells[cellKey(ch.r, ch.c)];
    }
    this.redoStack.push(entry);
    this.wb.notify();
  }

  redo() {
    const entry = this.redoStack.pop();
    if (!entry) return;
    const sheet = this.wb.sheet(entry.si);
    for (const ch of entry.changes) {
      if (ch.after) sheet.cells[cellKey(ch.r, ch.c)] = JSON.parse(JSON.stringify(ch.after));
      else delete sheet.cells[cellKey(ch.r, ch.c)];
    }
    this.undoStack.push(entry);
    this.wb.notify();
  }

  deleteSelection() {
    const s = this.sel;
    const coords: Array<[number, number]> = [];
    const sheet = this.sheet();
    for (const key of Object.keys(sheet.cells)) {
      const [r, c] = key.split(':').map(Number);
      if (r >= s.sr && r <= s.er && c >= s.sc && c <= s.ec) coords.push([r, c]);
    }
    if (!coords.length) return;
    const before = this.snapshotCells(this.si, coords);
    this.wb.clearRange(this.si, s.sr, s.sc, s.er, s.ec);
    this.pushUndo(this.si, before);
  }

  toggleStyle(prop: 'bold' | 'italic') {
    const anchor = this.wb.getCell(this.si, this.sel.ar, this.sel.ac);
    this.applyStyleToSelection({ [prop]: !anchor?.s?.[prop] });
  }

  applyStyleToSelection(style: NonNullable<Cell['s']>) {
    const s = this.sel;
    // Clamp huge selections (whole rows/cols) to the used range so styling
    // doesn't materialize hundreds of thousands of empty cells.
    const used = this.wb.usedRange(this.si);
    const er = Math.min(s.er, Math.max(used.rows - 1, s.sr), s.sr + 9999);
    const ec = Math.min(s.ec, Math.max(used.cols - 1, s.sc), s.sc + 255);
    const coords: Array<[number, number]> = [];
    for (let r = s.sr; r <= er; r++) for (let c = s.sc; c <= ec; c++) coords.push([r, c]);
    const before = this.snapshotCells(this.si, coords);
    this.wb.applyStyle(this.si, s.sr, s.sc, er, ec, style);
    this.pushUndo(this.si, before);
  }

  private applyFill(target: { sr: number; sc: number; er: number; ec: number }) {
    const s = this.sel;
    const coords: Array<[number, number]> = [];
    for (let r = target.sr; r <= target.er; r++) {
      for (let c = target.sc; c <= target.ec; c++) {
        if (r >= s.sr && r <= s.er && c >= s.sc && c <= s.ec) continue;
        coords.push([r, c]);
      }
    }
    if (!coords.length) return;
    const before = this.snapshotCells(this.si, coords);
    const srcRows = s.er - s.sr + 1;
    const srcCols = s.ec - s.sc + 1;
    const updates: Array<{ r: number; c: number; input: string }> = [];
    for (const [r, c] of coords) {
      const srcR = s.sr + (((r - s.sr) % srcRows) + srcRows) % srcRows;
      const srcC = s.sc + (((c - s.sc) % srcCols) + srcCols) % srcCols;
      const cell = this.wb.getCell(this.si, srcR, srcC);
      if (!cell) {
        updates.push({ r, c, input: '' });
        continue;
      }
      if (cell.f !== undefined) {
        updates.push({ r, c, input: '=' + adjustFormula(cell.f, r - srcR, c - srcC) });
      } else {
        const v = cell.v;
        if (typeof v === 'number' && srcRows * srcCols === 1 && target.er > s.er) {
          updates.push({ r, c, input: String(v + (r - srcR)) });
        } else {
          updates.push({ r, c, input: this.wb.getInput(this.si, srcR, srcC) });
        }
      }
    }
    this.wb.setCells(this.si, updates);
    this.pushUndo(this.si, before);
    this.setSelection(target.sr, target.sc, target.er, target.ec, s.ar, s.ac);
  }

  private selectionTSV(): string {
    const s = this.sel;
    const er = Math.min(s.er, s.sr + 9999);
    const ec = Math.min(s.ec, s.sc + 255);
    const lines: string[] = [];
    for (let r = s.sr; r <= er; r++) {
      const row: string[] = [];
      for (let c = s.sc; c <= ec; c++) row.push(this.wb.getDisplay(this.si, r, c));
      lines.push(row.join('\t'));
    }
    return lines.join('\n');
  }

  private captureClip(cut: boolean) {
    const s = this.sel;
    const er = Math.min(s.er, s.sr + 9999);
    const ec = Math.min(s.ec, s.sc + 255);
    const cells: Array<Array<{ f?: string; v?: Cell['v']; s?: Cell['s'] }>> = [];
    for (let r = s.sr; r <= er; r++) {
      const row: Array<{ f?: string; v?: Cell['v']; s?: Cell['s'] }> = [];
      for (let c = s.sc; c <= ec; c++) {
        const cell = this.wb.getCell(this.si, r, c);
        row.push(cell ? JSON.parse(JSON.stringify(cell)) : {});
      }
      cells.push(row);
    }
    const text = this.selectionTSV();
    this.internalClip = { text, cells, origin: { si: this.si, sr: s.sr, sc: s.sc }, cut };
    return text;
  }

  private gridHasFocus(): boolean {
    const ae = document.activeElement;
    return ae === this.scroller || ae === this.clip;
  }

  clearHistory() {
    this.undoStack = [];
    this.redoStack = [];
    this.internalClip = null;
  }

  private onCopy = (e: ClipboardEvent) => {
    if (this.editing || !this.gridHasFocus()) return;
    e.preventDefault();
    e.clipboardData?.setData('text/plain', this.captureClip(false));
  };

  private onCut = (e: ClipboardEvent) => {
    if (this.editing || !this.gridHasFocus()) return;
    e.preventDefault();
    e.clipboardData?.setData('text/plain', this.captureClip(true));
    this.deleteSelection();
  };

  private onPaste = (e: ClipboardEvent) => {
    if (this.editing || !this.gridHasFocus()) return;
    e.preventDefault();
    if (document.activeElement === this.clip) this.focus();
    const text = e.clipboardData?.getData('text/plain') ?? '';
    const s = this.sel;
    if (this.internalClip && this.internalClip.text === text) {
      const src = this.internalClip;
      const rows = src.cells.length;
      const cols = src.cells[0]?.length || 0;
      const coords: Array<[number, number]> = [];
      for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) coords.push([s.ar + r, s.ac + c]);
      const before = this.snapshotCells(this.si, coords);
      const updates: Array<{ r: number; c: number; input?: string; v?: Cell['v']; f?: string; s?: Cell['s'] }> = [];
      const dr = s.ar - src.origin.sr;
      const dc = s.ac - src.origin.sc;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const cell = src.cells[r][c];
          const tr = s.ar + r;
          const tc = s.ac + c;
          if (cell.f !== undefined) {
            const adj = src.cut ? cell.f : adjustFormula(cell.f, dr, dc);
            updates.push({ r: tr, c: tc, f: adj, s: cell.s });
          } else if (cell.v !== undefined) {
            updates.push({ r: tr, c: tc, v: cell.v, s: cell.s });
          } else {
            updates.push({ r: tr, c: tc, input: '' });
          }
        }
      }
      this.wb.setCells(this.si, updates);
      this.pushUndo(this.si, before);
      this.setSelection(s.ar, s.ac, Math.min(s.ar + rows - 1, this.nRows - 1), Math.min(s.ac + cols - 1, this.nCols - 1), s.ar, s.ac);
      return;
    }
    if (!text) return;
    const lines = text.replace(/\r\n?/g, '\n').split('\n');
    if (lines[lines.length - 1] === '') lines.pop();
    const grid = lines.map((l) => l.split('\t'));
    const coords: Array<[number, number]> = [];
    grid.forEach((row, r) => row.forEach((_, c) => coords.push([s.ar + r, s.ac + c])));
    const before = this.snapshotCells(this.si, coords);
    const updates = grid.flatMap((row, r) => row.map((val, c) => ({ r: s.ar + r, c: s.ac + c, input: val })));
    this.wb.setCells(this.si, updates);
    this.pushUndo(this.si, before);
    this.setSelection(s.ar, s.ac, Math.min(s.ar + grid.length - 1, this.nRows - 1), Math.min(s.ac + (grid[0]?.length || 1) - 1, this.nCols - 1), s.ar, s.ac);
  };
}

export { cellName, colName };
